import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, Clock, Circle, AlertCircle, Database, GitBranch, Zap, BarChart3,
  Shield, FileText, ChevronDown, ChevronRight, Lock, Search, ListTodo, CheckCheck, Mail,
  CreditCard, TestTube
} from 'lucide-react';
import api from '../../api.js';
import FeatureDetailModal from './FeatureDetailModal.jsx';

// Icon mapping - defined outside component to avoid recreation
const ICON_MAP = {
  Database,
  GitBranch,
  BarChart3,
  Shield,
  Zap,
  CheckCircle,
  Lock,
  Mail,
  CreditCard,
  TestTube,
  FileText,
};

// Color classes mapping - defined outside component
const COLOR_MAP = {
  '#dc2626': { bg: 'bg-red-50', border: 'border-red-200', hover: 'hover:bg-red-100', icon: 'bg-red-500', text: 'text-red-900', subtext: 'text-red-700', progress: 'text-red-600' },
  '#3b82f6': { bg: 'bg-blue-50', border: 'border-blue-200', hover: 'hover:bg-blue-100', icon: 'bg-blue-500', text: 'text-blue-900', subtext: 'text-blue-700', progress: 'text-blue-600' },
  '#10b981': { bg: 'bg-green-50', border: 'border-green-200', hover: 'hover:bg-green-100', icon: 'bg-green-500', text: 'text-green-900', subtext: 'text-green-700', progress: 'text-green-600' },
  '#f59e0b': { bg: 'bg-amber-50', border: 'border-amber-200', hover: 'hover:bg-amber-100', icon: 'bg-amber-500', text: 'text-amber-900', subtext: 'text-amber-700', progress: 'text-amber-600' },
  '#8b5cf6': { bg: 'bg-purple-50', border: 'border-purple-200', hover: 'hover:bg-purple-100', icon: 'bg-purple-500', text: 'text-purple-900', subtext: 'text-purple-700', progress: 'text-purple-600' },
  '#6366f1': { bg: 'bg-indigo-50', border: 'border-indigo-200', hover: 'hover:bg-indigo-100', icon: 'bg-indigo-500', text: 'text-indigo-900', subtext: 'text-indigo-700', progress: 'text-indigo-600' },
  'red': { bg: 'bg-red-50', border: 'border-red-200', hover: 'hover:bg-red-100', icon: 'bg-red-500', text: 'text-red-900', subtext: 'text-red-700', progress: 'text-red-600' },
  'blue': { bg: 'bg-blue-50', border: 'border-blue-200', hover: 'hover:bg-blue-100', icon: 'bg-blue-500', text: 'text-blue-900', subtext: 'text-blue-700', progress: 'text-blue-600' },
  'green': { bg: 'bg-green-50', border: 'border-green-200', hover: 'hover:bg-green-100', icon: 'bg-green-500', text: 'text-green-900', subtext: 'text-green-700', progress: 'text-green-600' },
  'amber': { bg: 'bg-amber-50', border: 'border-amber-200', hover: 'hover:bg-amber-100', icon: 'bg-amber-500', text: 'text-amber-900', subtext: 'text-amber-700', progress: 'text-amber-600' },
  'purple': { bg: 'bg-purple-50', border: 'border-purple-200', hover: 'hover:bg-purple-100', icon: 'bg-purple-500', text: 'text-purple-900', subtext: 'text-purple-700', progress: 'text-purple-600' },
  'indigo': { bg: 'bg-indigo-50', border: 'border-indigo-200', hover: 'hover:bg-indigo-100', icon: 'bg-indigo-500', text: 'text-indigo-900', subtext: 'text-indigo-700', progress: 'text-indigo-600' },
  'emerald': { bg: 'bg-emerald-50', border: 'border-emerald-200', hover: 'hover:bg-emerald-100', icon: 'bg-emerald-500', text: 'text-emerald-900', subtext: 'text-emerald-700', progress: 'text-emerald-600' },
  'violet': { bg: 'bg-violet-50', border: 'border-violet-200', hover: 'hover:bg-violet-100', icon: 'bg-violet-500', text: 'text-violet-900', subtext: 'text-violet-700', progress: 'text-violet-600' },
  'cyan': { bg: 'bg-cyan-50', border: 'border-cyan-200', hover: 'hover:bg-cyan-100', icon: 'bg-cyan-500', text: 'text-cyan-900', subtext: 'text-cyan-700', progress: 'text-cyan-600' },
  'slate': { bg: 'bg-slate-50', border: 'border-slate-200', hover: 'hover:bg-slate-100', icon: 'bg-slate-500', text: 'text-slate-900', subtext: 'text-slate-700', progress: 'text-slate-600' },
};

// Helper functions defined outside component
const getStatusIcon = (status) => {
  switch (status) {
    case 'complete':
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    case 'partial':
      return <Clock className="w-5 h-5 text-yellow-600" />;
    case 'not-started':
      return <Circle className="w-5 h-5 text-gray-400" />;
    default:
      return <AlertCircle className="w-5 h-5 text-red-600" />;
  }
};

const getStatusBadge = (status) => {
  const badges = {
    complete: 'bg-green-100 text-green-800 border-green-300',
    partial: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'not-started': 'bg-gray-100 text-gray-600 border-gray-300',
  };
  const labels = {
    complete: 'Complet',
    partial: 'Partiel',
    'not-started': 'Non démarré',
  };
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${badges[status]}`}>
      {labels[status]}
    </span>
  );
};

const getProgressColor = (progress) => {
  if (progress >= 80) return 'bg-green-500';
  if (progress >= 50) return 'bg-yellow-500';
  if (progress >= 20) return 'bg-orange-500';
  return 'bg-red-500';
};

const getCategoryClasses = (colorValue) => {
  return COLOR_MAP[colorValue] || COLOR_MAP['blue'];
};

// Feature Row Component - defined outside main component
function FeatureRow({ feature, compact = false, onSelect }) {
  return (
    <div
      onClick={() => onSelect(feature)}
      className={`${compact ? 'p-3' : 'p-4'} hover:bg-blue-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {getStatusIcon(feature.status)}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-medium text-gray-900 ${compact ? 'text-sm' : ''}`}>{feature.name}</span>
              {getStatusBadge(feature.status)}
            </div>
            {!compact && feature.notes && (
              <p className="text-sm text-gray-500 mt-1 truncate">{feature.notes}</p>
            )}
          </div>
        </div>
        <div className="text-right ml-4 flex-shrink-0">
          <div className={`font-bold text-gray-900 ${compact ? 'text-sm' : 'text-lg'}`}>{feature.progress}%</div>
        </div>
      </div>
      {!compact && (
        <div className="mt-2 bg-gray-200 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-full ${getProgressColor(feature.progress)} transition-all duration-500`}
            style={{ width: `${feature.progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

// Category Accordion Component - defined outside main component
function CategoryAccordion({ category, features, isExpanded, onToggle, section, onSelectFeature }) {
  const Icon = category.icon;
  const colors = getCategoryClasses(category.color);
  const categoryProgress = features.length > 0
    ? Math.round(features.reduce((sum, f) => sum + f.progress, 0) / features.length)
    : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-2">
      <button
        onClick={onToggle}
        className={`w-full px-4 py-3 flex items-center justify-between ${colors.bg} ${colors.hover} transition-colors`}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
          <div className={`p-1.5 ${colors.icon} rounded-lg`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <span className={`font-semibold ${colors.text}`}>{category.name}</span>
            <span className="ml-2 text-sm text-gray-500">({features.length})</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-24 bg-white rounded-full h-2 overflow-hidden">
            <div
              className={`h-full ${getProgressColor(categoryProgress)}`}
              style={{ width: `${categoryProgress}%` }}
            />
          </div>
          <span className={`text-sm font-bold ${colors.progress}`}>{categoryProgress}%</span>
        </div>
      </button>

      {isExpanded && (
        <div className="bg-white">
          {features.map((feature) => (
            <FeatureRow
              key={feature.id}
              feature={feature}
              compact={section === 'completed'}
              onSelect={onSelectFeature}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AppRadarView() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [featureCategories, setFeatureCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Main accordion states: remaining vs completed
  const [remainingExpanded, setRemainingExpanded] = useState(true);
  const [completedExpanded, setCompletedExpanded] = useState(false);

  // Sub-accordion states for categories within each section
  const [expandedRemainingCategories, setExpandedRemainingCategories] = useState(new Set());
  const [expandedCompletedCategories, setExpandedCompletedCategories] = useState(new Set());

  // Load feature tracking data from API
  useEffect(() => {
    const loadFeatureData = async () => {
      try {
        setLoading(true);
        const response = await api.featureTracking.getAll();

        if (response.ok && response.data) {
          // Group features by category
          const categoryMap = {};
          response.data.forEach(item => {
            if (!categoryMap[item.category_id]) {
              let icon = Database;
              if (item.category_icon) {
                icon = ICON_MAP[item.category_icon] || ICON_MAP[item.category_icon.trim()] || Database;
              }

              categoryMap[item.category_id] = {
                id: item.category_id,
                name: item.category_name,
                icon: icon,
                color: item.category_color,
                features: []
              };
            }
            categoryMap[item.category_id].features.push({
              id: item.id,
              name: item.feature_name,
              status: item.status,
              progress: item.progress,
              notes: item.notes,
              spec_reference: item.spec_reference,
              category_id: item.category_id,
              category_name: item.category_name
            });
          });

          const categories = Object.values(categoryMap);
          setFeatureCategories(categories);

          // Expand first remaining category by default
          const remainingCats = categories.filter(cat =>
            cat.features.some(f => f.status !== 'complete')
          );
          if (remainingCats.length > 0) {
            setExpandedRemainingCategories(new Set([remainingCats[0].id]));
          }
        }
      } catch (err) {
        console.error('Error loading feature tracking data:', err);
        setError('Failed to load feature tracking data');
      } finally {
        setLoading(false);
      }
    };

    loadFeatureData();
  }, []);

  // Handler for selecting a feature to show detail modal
  const handleSelectFeature = useCallback((feature) => {
    setSelectedFeature(feature);
    setShowDetailModal(true);
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 mt-1" />
          <div>
            <h3 className="text-lg font-bold text-red-900 mb-2">Erreur</h3>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Filter by category
  const filteredCategories = selectedCategory === 'all'
    ? featureCategories
    : featureCategories.filter(cat => cat.id === selectedCategory);

  // Apply search filter
  const applySearch = (features) => {
    if (!searchTerm) return features;
    const term = searchTerm.toLowerCase();
    return features.filter(f =>
      f.name.toLowerCase().includes(term) ||
      (f.notes && f.notes.toLowerCase().includes(term))
    );
  };

  // Split features into remaining and completed
  const getRemainingFeatures = (category) => {
    const filtered = applySearch(category.features);
    return filtered.filter(f => f.status !== 'complete');
  };

  const getCompletedFeatures = (category) => {
    const filtered = applySearch(category.features);
    return filtered.filter(f => f.status === 'complete');
  };

  // Get categories with remaining/completed features
  const categoriesWithRemaining = filteredCategories
    .map(cat => ({ ...cat, filteredFeatures: getRemainingFeatures(cat) }))
    .filter(cat => cat.filteredFeatures.length > 0);

  const categoriesWithCompleted = filteredCategories
    .map(cat => ({ ...cat, filteredFeatures: getCompletedFeatures(cat) }))
    .filter(cat => cat.filteredFeatures.length > 0);

  // Calculate overall stats
  const allFeatures = featureCategories.flatMap(cat => cat.features);
  const totalFeatures = allFeatures.length;
  const completeFeatures = allFeatures.filter(f => f.status === 'complete').length;
  const partialFeatures = allFeatures.filter(f => f.status === 'partial').length;
  const notStartedFeatures = allFeatures.filter(f => f.status === 'not-started').length;
  const averageProgress = totalFeatures > 0 ? Math.round(allFeatures.reduce((sum, f) => sum + f.progress, 0) / totalFeatures) : 0;

  const totalRemaining = partialFeatures + notStartedFeatures;

  // Toggle category expansion
  const toggleRemainingCategory = (catId) => {
    const newSet = new Set(expandedRemainingCategories);
    if (newSet.has(catId)) {
      newSet.delete(catId);
    } else {
      newSet.add(catId);
    }
    setExpandedRemainingCategories(newSet);
  };

  const toggleCompletedCategory = (catId) => {
    const newSet = new Set(expandedCompletedCategories);
    if (newSet.has(catId)) {
      newSet.delete(catId);
    } else {
      newSet.add(catId);
    }
    setExpandedCompletedCategories(newSet);
  };

  return (
    <div className="space-y-6">
      {/* Header with Overall Stats */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold">App Radar</h1>
            <p className="text-blue-100 mt-1">Statut global du projet FleetManager Max</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
            <div className="text-5xl font-bold">{averageProgress}%</div>
            <div className="text-sm text-blue-100">Progression globale</div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{totalFeatures}</div>
            <div className="text-sm text-blue-100">Total Features</div>
          </div>
          <div className="bg-green-500/30 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{completeFeatures}</div>
            <div className="text-sm text-blue-100">Complets</div>
          </div>
          <div className="bg-yellow-500/30 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{partialFeatures}</div>
            <div className="text-sm text-blue-100">Partiels</div>
          </div>
          <div className="bg-gray-500/30 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{notStartedFeatures}</div>
            <div className="text-sm text-blue-100">Non démarrés</div>
          </div>
        </div>
      </div>

      {/* Search and Category Filter */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchTerm && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
              ⌘K
            </span>
          )}
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
              selectedCategory === 'all'
                ? 'bg-blue-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Toutes les catégories
          </button>
          {featureCategories.map(cat => {
            const Icon = cat.icon;
            const colors = getCategoryClasses(cat.color);
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                  selectedCategory === cat.id
                    ? `${colors.icon} text-white shadow-lg`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* MAIN SECTION: Remaining Work (Accordion) */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <button
          onClick={() => setRemainingExpanded(!remainingExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 transition-colors border-b border-amber-200"
        >
          <div className="flex items-center gap-4">
            {remainingExpanded ? (
              <ChevronDown className="w-6 h-6 text-amber-600" />
            ) : (
              <ChevronRight className="w-6 h-6 text-amber-600" />
            )}
            <div className="p-2 bg-amber-500 rounded-lg">
              <ListTodo className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h2 className="text-xl font-bold text-amber-900">Travail Restant</h2>
              <p className="text-sm text-amber-700">
                {totalRemaining} fonctionnalité{totalRemaining > 1 ? 's' : ''} à compléter
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-3xl font-bold text-amber-600">{totalRemaining}</div>
              <div className="text-xs text-amber-600">restantes</div>
            </div>
          </div>
        </button>

        {remainingExpanded && (
          <div className="p-4 bg-gray-50">
            {categoriesWithRemaining.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Tout est terminé ! Aucune tâche restante.</p>
              </div>
            ) : (
              categoriesWithRemaining.map(cat => (
                <CategoryAccordion
                  key={cat.id}
                  category={cat}
                  features={cat.filteredFeatures}
                  isExpanded={expandedRemainingCategories.has(cat.id)}
                  onToggle={() => toggleRemainingCategory(cat.id)}
                  section="remaining"
                  onSelectFeature={handleSelectFeature}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* MAIN SECTION: Completed Work (Accordion) */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <button
          onClick={() => setCompletedExpanded(!completedExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 transition-colors border-b border-green-200"
        >
          <div className="flex items-center gap-4">
            {completedExpanded ? (
              <ChevronDown className="w-6 h-6 text-green-600" />
            ) : (
              <ChevronRight className="w-6 h-6 text-green-600" />
            )}
            <div className="p-2 bg-green-500 rounded-lg">
              <CheckCheck className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h2 className="text-xl font-bold text-green-900">Travail Terminé</h2>
              <p className="text-sm text-green-700">
                {completeFeatures} fonctionnalité{completeFeatures > 1 ? 's' : ''} complétée{completeFeatures > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-3xl font-bold text-green-600">{completeFeatures}</div>
              <div className="text-xs text-green-600">terminées</div>
            </div>
          </div>
        </button>

        {completedExpanded && (
          <div className="p-4 bg-gray-50">
            {categoriesWithCompleted.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Circle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Aucune fonctionnalité terminée pour le moment.</p>
              </div>
            ) : (
              categoriesWithCompleted.map(cat => (
                <CategoryAccordion
                  key={cat.id}
                  category={cat}
                  features={cat.filteredFeatures}
                  isExpanded={expandedCompletedCategories.has(cat.id)}
                  onToggle={() => toggleCompletedCategory(cat.id)}
                  section="completed"
                  onSelectFeature={handleSelectFeature}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Légende</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <div className="font-semibold text-gray-900">Complet</div>
              <div className="text-sm text-gray-600">Fonctionnalité complètement implémentée</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-yellow-600" />
            <div>
              <div className="font-semibold text-gray-900">Partiel</div>
              <div className="text-sm text-gray-600">Implémentation partielle ou basique</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Circle className="w-6 h-6 text-gray-400" />
            <div>
              <div className="font-semibold text-gray-900">Non démarré</div>
              <div className="text-sm text-gray-600">Pas encore implémenté</div>
            </div>
          </div>
        </div>
      </div>

      {/* Specification Reference */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <FileText className="w-6 h-6 text-blue-600 mt-1" />
          <div>
            <h3 className="text-lg font-bold text-blue-900 mb-2">Référence Spécification</h3>
            <p className="text-sm text-blue-800">
              Basé sur <strong>fleetmanager_max.yaml</strong> - Version 3.1 (Phase 3: Insurance + SOP + Metrics)
            </p>
            <ul className="mt-2 text-sm text-blue-700 space-y-1">
              <li>• 28 Entités (tables de base de données)</li>
              <li>• 5 Workflows (machines à états)</li>
              <li>• 28 KPIs métier</li>
              <li>• 48 Règles métier</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Feature Detail Modal */}
      <FeatureDetailModal
        feature={selectedFeature}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedFeature(null);
        }}
      />
    </div>
  );
}

export default AppRadarView;
